import { Element, useNode } from "@craftjs/core";
import { rem } from "polished";
import { useContext } from "react";
import { Actions } from "src/types";
import { ThemeContext } from "styled-components";

import TopLevelFloatingOptions from "@/components/editor/block-outline/floating-options/top-level-floating-component";
import type { ImagePosition } from "@/components/editor/image-positioner/image-positioner-component";
import Button from "@/components/editor/primitives/button/button-component";
import RichText from "@/components/editor/primitives/rich-text";
import Text from "@/components/editor/primitives/text/text-component";
import {
  BlockBackgroundColorWrapper,
  BlockMaxWidthWrapper,
} from "@/components/editor/shared/block-wrapper";
import {
  DEFAULT_BUTTON_TEXT,
  DEFAULT_HEADING_TEXT,
  DEFAULT_SUBHEADING_TEXT,
} from "@/components/editor/shared/constants";
import {
  blueLogo,
  greenLogo,
  purpleLogo,
} from "@/components/editor/shared/header/assets";
import formatLogoSize from "@/components/editor/shared/header/format-logo-size";
import HeaderSettings, {
  ImageFit,
} from "@/components/editor/shared/header/header-settings-component";
import { ColorLabels } from "@/components/editor/utils/use-color";
import useEvent from "@/hooks/use-event";
import useRichTextCompatible from "@/hooks/use-rich-text-compatible";
import humanizeDateRange from "@/utils/dates/humanizeDateRange";

import {
  ButtonWrapper,
  Container,
  ContentWrapper,
  CTATextWrapper,
  HeadingWrapper,
  Image,
  ImageWrapper,
  LeftColumn,
  Logo,
  LogoWrapper,
  PaddingWrapper,
  RightColumn,
  SubheadingWrapper,
} from "./header-option-2-component-styles";

type Props = {
  backgroundImage?: string;
  backgroundImageAlt?: string;
  backgroundPosition?: ImagePosition;
  isLogoShowing?: boolean;
  logo?: string;
  logoSize?: number;
  imageFit?: ImageFit;
};

const CustomHeaderSettings = () => {
  return (
    <HeaderSettings
      optimalImageSize="1400x400px"
      backgroundImageSize={{
        width: "288",
        height: "72",
      }}
    />
  );
};

export default function HeaderOption2(props: Props) {
  const {
    connectors: { connect },
    id: nodeId,
  } = useNode();
  const { event } = useEvent();
  const theme = useContext(ThemeContext);
  const backgroundColor = theme.colors[ColorLabels.BACKGROUND];
  const showBlueLogo = backgroundColor === "#ffffff";

  const defaultLogoImage =
    backgroundColor === "#1c1b21"
      ? purpleLogo
      : showBlueLogo
      ? blueLogo
      : greenLogo;

  const isRichTextCompatible = useRichTextCompatible(nodeId, [
    "heading",
    "cta-text",
    "subheading",
  ]);

  return (
    <BlockBackgroundColorWrapper ref={connect} id={nodeId}>
      <BlockMaxWidthWrapper maxWidth={rem(1440)}>
        <PaddingWrapper>
          <ImageWrapper>
            <Image
              src={props.backgroundImage}
              alt={props.backgroundImageAlt || ""}
              $position={props.backgroundPosition || "center center"}
              imageFit={props.imageFit}
            />
          </ImageWrapper>
          <Container>
            <ContentWrapper isLogoShowing={props.isLogoShowing}>
              <LeftColumn isLogoShowing={props.isLogoShowing}>
                {props.isLogoShowing && (
                  <LogoWrapper>
                    <Logo
                      draggable="false"
                      src={props.logo || defaultLogoImage}
                      style={{
                        width: formatLogoSize(props.logoSize),
                        height: "auto",
                      }}
                      isLogoResized={!isNaN(props.logoSize)}
                      alt="Header Logo"
                    />
                  </LogoWrapper>
                )}
                <HeadingWrapper>
                  {isRichTextCompatible ? (
                    <Element
                      id="heading"
                      is={RichText}
                      text={`<h2>${event.name || DEFAULT_HEADING_TEXT}</h2>`}
                    />
                  ) : (
                    <Element
                      id="heading"
                      is={Text}
                      element="h1"
                      text={event.name || DEFAULT_HEADING_TEXT}
                      pattern="headingOne"
                    />
                  )}
                </HeadingWrapper>
              </LeftColumn>
              <RightColumn>
                {isRichTextCompatible ? (
                  <>
                    <CTATextWrapper>
                      <Element
                        id="cta-text"
                        is={RichText}
                        text={`<h5>${humanizeDateRange(
                          event.time_start,
                          event.time_end,
                          event.standard_timezone
                        ).join(" - ")}</h5>`}
                      />
                    </CTATextWrapper>
                    <SubheadingWrapper>
                      <Element
                        id="subheading"
                        is={RichText}
                        text={`<p>${DEFAULT_SUBHEADING_TEXT}</p>`}
                      />
                    </SubheadingWrapper>
                  </>
                ) : (
                  <>
                    <CTATextWrapper>
                      <Element
                        id="cta-text"
                        is={Text}
                        element="h4"
                        pattern="headingFour"
                        text={humanizeDateRange(
                          event.time_start,
                          event.time_end,
                          event.standard_timezone
                        ).join(" - ")}
                      />
                    </CTATextWrapper>
                    <SubheadingWrapper>
                      <Element
                        id="subheading"
                        is={Text}
                        pattern="paragraph"
                        text={DEFAULT_SUBHEADING_TEXT}
                      />
                    </SubheadingWrapper>
                  </>
                )}

                <ButtonWrapper>
                  <Element
                    id="cta-button"
                    is={Button}
                    text={DEFAULT_BUTTON_TEXT}
                    action={{
                      kind: Actions.TicketSelector,
                    }}
                  />
                </ButtonWrapper>
              </RightColumn>
            </ContentWrapper>
          </Container>
        </PaddingWrapper>
      </BlockMaxWidthWrapper>
    </BlockBackgroundColorWrapper>
  );
}

HeaderOption2.craft = {
  custom: {
    displayName: "Header option #2",
  },
  related: {
    settings: CustomHeaderSettings,
    leftOutlineOptions: TopLevelFloatingOptions,
  },
};
